import { ElementType, ReactNode } from "react";
import { Typography, TypographyProps } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { BaseFormLabelClasses, getBaseFormLabelClass } from "./baseFormLabelClasses";
import { composeClasses, Stack } from "@kaltura/mediaspace-shared-styled";
import clsx from "clsx";

export { baseFormLabelClasses } from "./baseFormLabelClasses";

export interface BaseFormLabelProps extends Pick<TypographyProps, "variant"> {
    className?: string;
    classes?: Partial<BaseFormLabelClasses>;
    text: string;
    htmlFor?: string;
    id?: string;
    required?: boolean;
    additionalInfo?: string;
    /** replace "label" with another tag or component */
    component?: ElementType;
    /** render additional action components next to the label */
    actions?: ReactNode;
}

const StyledLabel = styled("label")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        display: "block",
        marginBottom: theme.spacing(1),
    },
}));

const StyledTypography = styled(
    Typography,
    {
        shouldForwardProp(propName) {
            return propName !== "additionalInfo";
        },
    }
)<{ additionalInfo?: boolean }>(({ theme, additionalInfo }) => ({
    color: theme.kaltura.palette.tone2,
    ...(
        additionalInfo && {
            ...theme.kaltura.typography.formLabel,
            fontSize: theme.typography.pxToRem(13),
        }
    ),
}));

const useUtilityClasses = ({ classes }: Partial<BaseFormLabelProps>) => {
    const slots = {
        root: ["root"],
        label: ["label"],
        labelInfo: ["labelInfo"],
    };

    return composeClasses(slots, getBaseFormLabelClass, classes);
};

/**
 * Form Label
 */
export const BaseFormLabel = (props: BaseFormLabelProps) => {
    const { component, htmlFor, id, text, required, className, variant = "body2", additionalInfo, actions } = props;
    const classes = useUtilityClasses(props);

    return (
        <StyledLabel as={component} htmlFor={htmlFor} id={id} className={clsx(classes.root, className)}>
            <Stack direction={"row"} gap={3} alignItems={"center"} justifyContent={"space-between"}>
                <StyledTypography variant={variant} component={"span"} className={classes.label}>
                    {HtmlReactParser(String(text))}
                    {required ? " *" : ""}
                </StyledTypography>

                {actions && (
                    <Stack direction={"row"} gap={1} alignItems={"center"}>
                        {actions}
                    </Stack>
                )}
            </Stack>
            {additionalInfo && (
                <StyledTypography component={"span"} className={classes.labelInfo} additionalInfo={true}>
                    {HtmlReactParser(String(additionalInfo))}
                </StyledTypography>
            )}
        </StyledLabel>
    );
};
